'use client'

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, type ErrorComponentProps } from '@tanstack/react-router'
import { Button } from '@workspace/ui/components/Button'
import { Home, RefreshCw } from 'lucide-react'

export function RouteErrorFallback({ error, reset }: ErrorComponentProps) {
    const { t } = useTranslation()
    const navigate = useNavigate()

    useEffect(
        function logRouteError() {
            console.error('[RouteError]', error)
        },
        [error],
    )

    const handleRetry = () => {
        reset()
    }

    const handleGoHome = () => {
        reset()
        navigate({ to: '/' })
    }

    return (
        <div className="flex min-h-screen items-center justify-center flex-col p-4 bg-background">
            <div className="w-full max-w-[480px] flex flex-col items-center gap-6 text-center">
                <img src="/404-error.png" alt="" className="h-full max-h-[360px] w-auto" />

                <div className="space-y-3">
                    <h1 className="text-4xl font-bold text-foreground">{t('errorPage.title')}</h1>
                    <p className="text-base text-muted-foreground">{t('errorPage.message')}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto mt-2">
                    <Button variant="default" size="lg" onPress={handleRetry} className="gap-2">
                        <RefreshCw className="h-4 w-4" />
                        {t('errorPage.retryButton')}
                    </Button>
                    <Button variant="outline" size="lg" onPress={handleGoHome} className="gap-2">
                        <Home className="h-4 w-4" />
                        {t('errorPage.goHomeButton')}
                    </Button>
                </div>

                {import.meta.env.DEV && error?.message ? (
                    <details className="w-full text-left rounded-md border border-destructive/30 bg-destructive/5 p-3">
                        <summary className="cursor-pointer text-sm text-muted-foreground">
                            {t('errorPage.devDetails')}
                        </summary>
                        <pre className="mt-2 overflow-auto text-xs text-destructive whitespace-pre-wrap break-words">
                            {error.message}
                        </pre>
                    </details>
                ) : null}
            </div>
        </div>
    )
}
